import { createSlice } from "@reduxjs/toolkit";
import {
    adminupdateVerification,
    createSupportTicketNotes,
    createUserCategories,
    deleteUserCategories,
    getAllUserCounts,
    getAllUsers,
    getLocalProfessionals,
    getInternationalProfessionals,
    getEmergencyProfessionals,
    getAllVerifications,
    getUserCategories,
    getUserSupportTicketNotes,
    updateUserCategories,
    getAllCountries,
    getCountryById,
    createCountry,
    updateUserStatus,
    suspendUser,
    activateUser,
    deleteUser,
    changeUserPassword,
    getAdminDashboardStats,
    getAdminRevenueStats,
    getAdminBusinessAnalytics,
    getAdminUserAnalytics,
    getAdminGrowthAnalytics,
    getQuoteClients,
    UpdateUserProfile,
} from "../Actions";
import { AdminState } from "../../modules";
import { StatusEnum } from "../../modules/shared/model";

const initialState: AdminState = {
    supportTicketNotes: [],
    allUsers: {
        data: [],
        totalCount: 0,
        hasNextPage: false,
        currentPage: 1,
        totalPages: 1,
    },
    localProfessionals: {
        data: [],
        totalCount: 0,
        hasNextPage: false,
        currentPage: 1,
        totalPages: 1,
        locationFilters: {
            provinces: [],
            citiesByProvince: {},
            municipalities: []
        }
    },
    internationalProfessionals: {
        data: [],
        totalCount: 0,
        hasNextPage: false,
        currentPage: 1,
        totalPages: 1,
    },
    emergencyProfessionals: {
        data: [],
        totalCount: 0,
        hasNextPage: false,
        currentPage: 1,
        totalPages: 1,
    },
    userCounts: null,
    dashboardStats: null,
    revenueStats: [],
    businessAnalytics: null,
    userAnalytics: null,
    growthAnalytics: null,
    categories: {
        data: [],
        totalCount: 0,
        hasNextPage: false,
        currentPage: 1,
        totalPages: 1
    },
    error: null,
    verifications: [],
    verificationsCount: 0,
    verificationHasNextPage: false,
    countries: [],
    quoteClients: {
        data: [],
        totalCount: 0,
        hasNextPage: false,
        currentPage: 1,
        totalPages: 1,
    },
};

const adminSlice = createSlice({
    name: "admin",
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(deleteUserCategories.fulfilled, (state, action) => {
                state.categories.data = state.categories.data.filter(item => item.id !== action.payload.id);
                state.categories.totalCount = Math.max(0, state.categories.totalCount - 1);
            })
            .addCase(getUserCategories.fulfilled, (state, action) => {
                if (action.payload) {
                    // Handle paginated response structure
                    if (action.payload.data && typeof action.payload.totalCount !== "undefined") {
                        // New paginated structure
                        const totalCount = action.payload.totalCount;
                        const currentPage = action.meta.arg.page || 1;
                        const limit = action.meta.arg.limit || 10;
                        const totalPages = Math.ceil(totalCount / limit);
                        state.categories = {
                            data: action.payload.data,
                            totalCount,
                            hasNextPage: currentPage < totalPages,
                            currentPage,
                            totalPages
                        };
                    } else {
                        // Backward compatibility for non-paginated response
                        state.categories = {
                            data: Array.isArray(action.payload) ? action.payload : [],
                            totalCount: Array.isArray(action.payload) ? action.payload.length : 0,
                            hasNextPage: false,
                            currentPage: 1,
                            totalPages: 1
                        };
                    }
                    state.error = null;
                } else {
                    state.categories = {
                        data: [],
                        totalCount: 0,
                        hasNextPage: false,
                        currentPage: 1,
                        totalPages: 1
                    };
                }
            })
            .addCase(getAllUserCounts.fulfilled, (state, action) => {
                if (action.payload) {
                    state.userCounts = {
                        totalUsers: action.payload.totalUsers || 0,
                        professionals: action.payload.professionals || 0,
                        internationalProfessionals: action.payload.internationalProfessionals || 0,
                        clients: action.payload.clients || 0,
                        internationalClients: action.payload.internationalClients || 0,
                        emergencyProfessionals: action.payload.emergencyProfessionals || 0,
                    };
                }
            })
            .addCase(getAdminDashboardStats.fulfilled, (state, action) => {
                if (action.payload) {
                    state.dashboardStats = action.payload;
                }
                state.error = null;
            })
            .addCase(getAdminDashboardStats.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            .addCase(getAdminRevenueStats.fulfilled, (state, action) => {
                if (action.payload) {
                    state.revenueStats = action.payload;
                }
                state.error = null;
            })
            .addCase(getAdminRevenueStats.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            .addCase(getAdminBusinessAnalytics.fulfilled, (state, action) => {
                if (action.payload) {
                    state.businessAnalytics = action.payload;
                }
                state.error = null;
            })
            .addCase(getAdminBusinessAnalytics.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            .addCase(getAdminUserAnalytics.fulfilled, (state, action) => {
                if (action.payload) {
                    state.userAnalytics = action.payload;
                }
                state.error = null;
            })
            .addCase(getAdminUserAnalytics.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            .addCase(getAdminGrowthAnalytics.fulfilled, (state, action) => {
                if (action.payload) {
                    state.growthAnalytics = action.payload;
                }
                state.error = null;
            })
            .addCase(getAdminGrowthAnalytics.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            .addCase(createUserCategories.fulfilled, (state, action: any) => {
                // Append new category to the existing state
                state.categories.data = [...state.categories.data, action.payload];
                state.categories.totalCount += 1;
            })
            .addCase(updateUserCategories.fulfilled, (state, action: any) => {
                // Find and update the specific category
                const updated = action.payload;
                const index = state.categories.data.findIndex(item => item.id === updated.id);
                if (index !== -1) {
                    state.categories.data[index] = updated;
                }
            })
            .addCase(getAllVerifications.fulfilled, (state, action) => {
                state.verifications = action.payload?.data || [];
                state.verificationsCount = action.payload?.totalCount || 0;
                state.verificationHasNextPage = action.payload?.hasNextPage || false;
                state.error = null;
            })
            .addCase(adminupdateVerification.fulfilled, (state, action) => {
                const updatedVerification = action.payload;
                if (!updatedVerification) return;

                const index = state.verifications.findIndex(v => v.id === updatedVerification.id);
                if (index !== -1) {
                    state.verifications[index] = updatedVerification;
                } else {
                    // optional: if not found, add it
                    state.verifications.push(updatedVerification);
                }
            })
            .addCase(getAllUsers.fulfilled, (state, action) => {
                const totalCount = action.payload.totalCount;
                const currentPage = action.meta.arg.page || 1;
                const limit = action.meta.arg.limit || 10;
                const totalPages = Math.ceil(totalCount / limit);
                state.allUsers = {
                    data: action.payload.data, // This will always contain only 10 users (current page)
                    totalCount,
                    hasNextPage: currentPage < totalPages,
                    currentPage,
                    totalPages
                };
            })
            .addCase(UpdateUserProfile.fulfilled, (state, action: any) => {
                const updatedUser = action.payload;
                if (!updatedUser?.id) {
                    return;
                }

                const index = state.allUsers.data.findIndex((user: any) => String(user.id) === String(updatedUser.id));
                if (index !== -1) {
                    state.allUsers.data[index] = {
                        ...state.allUsers.data[index],
                        ...updatedUser,
                    };
                }
            })
            .addCase(getLocalProfessionals.fulfilled, (state, action) => {
                const totalCount = action.payload.totalCount;
                const currentPage = action.meta.arg.page || 1;
                const limit = action.meta.arg.limit || 10;
                const totalPages = Math.ceil(totalCount / limit);
                state.localProfessionals = {
                    data: action.payload.data,
                    totalCount,
                    hasNextPage: currentPage < totalPages,
                    currentPage,
                    totalPages,
                    locationFilters: action.payload.locationFilters || {
                        provinces: [],
                        citiesByProvince: {},
                        municipalities: []
                    }
                };
            })
            .addCase(getInternationalProfessionals.fulfilled, (state, action) => {
                const totalCount = action.payload.totalCount;
                const currentPage = action.meta.arg.page || 1;
                const limit = action.meta.arg.limit || 10;
                const totalPages = Math.ceil(totalCount / limit);
                state.internationalProfessionals = {
                    data: action.payload.data,
                    totalCount,
                    hasNextPage: currentPage < totalPages,
                    currentPage,
                    totalPages
                };
            })
            .addCase(getEmergencyProfessionals.fulfilled, (state, action) => {
                const totalCount = action.payload.totalCount;
                const currentPage = action.meta.arg.page || 1;
                const limit = action.meta.arg.limit || 10;
                const totalPages = Math.ceil(totalCount / limit);
                state.emergencyProfessionals = {
                    data: action.payload.data,
                    totalCount,
                    hasNextPage: currentPage < totalPages,
                    currentPage,
                    totalPages,
                };
            })
            .addCase(getUserSupportTicketNotes.fulfilled, (state, action) => {
                state.supportTicketNotes = action.payload;
            })
            .addCase(createSupportTicketNotes.fulfilled, (state, action) => {
                if (action.payload) {
                    state.supportTicketNotes=[...state.supportTicketNotes,action.payload];
                }
            })
            .addCase(getAllCountries.fulfilled, (state, action) => {
                if (action.payload) {
                    const shouldAppend = action.meta.arg.append || false;
                    let newCountries: any[] = [];
                    
                    // Handle both paginated and non-paginated responses
                    if (action.payload.data && Array.isArray(action.payload.data)) {
                        newCountries = action.payload.data;
                    } else if (Array.isArray(action.payload)) {
                        // Handle non-paginated response (backward compatibility)
                        newCountries = action.payload;
                    }
                    
                    if (shouldAppend) {
                        // Append mode: Add new countries that don't already exist
                        const existingIds = new Set(state.countries.map((c: any) => c.id));
                        const uniqueNewCountries = newCountries.filter((c: any) => !existingIds.has(c.id));
                        state.countries = [...state.countries, ...uniqueNewCountries];
                    } else {
                        // Replace mode: Replace entire array
                        state.countries = newCountries;
                    }
                    
                    state.error = null;
                } else {
                    state.countries = [];
                }
            })
            .addCase(getCountryById.fulfilled, (state, action) => {
                // Append single country to the list if not already present
                if (action.payload) {
                    // Check if country already exists
                    const exists = state.countries.some((c: any) => c.id === action.payload.id);
                    
                    if (!exists) {
                        state.countries = [...state.countries, action.payload];
                    }
                }
            })
            .addCase(getCountryById.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            .addCase(createCountry.fulfilled, (state, action) => {
                if (action.payload) {
                    // Add the newly created country to the countries array
                    state.countries = [...state.countries, action.payload];
                }
            })
            .addCase(getUserCategories.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            // User Status Management Cases
            .addCase(updateUserStatus.fulfilled, (state, action) => {
                // Update the user status in the allUsers data
                const { userId, status } = action.payload;
                const userIndex = state.allUsers.data.findIndex(user => user.id === userId);
                if (userIndex !== -1) {
                    state.allUsers.data[userIndex].status = { id: status, name: StatusEnum[status] };
                }
                state.error = null;
            })
            .addCase(updateUserStatus.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            .addCase(suspendUser.fulfilled, (state, action) => {
                // Update the user status in the allUsers data
                const { userId, status } = action.payload;
                const userIndex = state.allUsers.data.findIndex(user => user.id === userId);
                if (userIndex !== -1) {
                    state.allUsers.data[userIndex].status = { id: status, name: StatusEnum[status] };
                }
                state.error = null;
            })
            .addCase(suspendUser.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            .addCase(activateUser.fulfilled, (state, action) => {
                // Update the user status in the allUsers data
                const { userId, status } = action.payload;
                const userIndex = state.allUsers.data.findIndex(user => user.id === userId);
                if (userIndex !== -1) {
                    state.allUsers.data[userIndex].status = { id: status, name: StatusEnum[status] };
                }
                state.error = null;
            })
            .addCase(activateUser.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            .addCase(deleteUser.fulfilled, (state, action) => {
                const { userId, status } = action.payload;
                const userIndex = state.allUsers.data.findIndex(user => user.id === userId);
                if (userIndex !== -1) {
                    state.allUsers.data[userIndex].status = { id: status, name: StatusEnum[status] };
                }
                state.error = null;
            })
            .addCase(deleteUser.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            // Change Password Cases
            .addCase(changeUserPassword.fulfilled, (state, action) => {
                state.error = null;
            })
            .addCase(changeUserPassword.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            // Quote Clients Cases
            .addCase(getQuoteClients.fulfilled, (state, action) => {
                const totalCount = action.payload.totalCount;
                const currentPage = action.meta.arg.page || 1;
                const limit = action.meta.arg.limit || 10;
                const totalPages = Math.ceil(totalCount / limit);
                state.quoteClients = {
                    data: action.payload.data,
                    totalCount,
                    hasNextPage: currentPage < totalPages,
                    currentPage,
                    totalPages
                };
                state.error = null;
            })
            .addCase(getQuoteClients.rejected, (state, action) => {
                state.error = action.payload as string;
            });
    },
});

export default adminSlice.reducer;
